import { Box, useTheme } from "@mui/material";
import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type FunctionComponent,
  type MouseEvent as ReactMouseEvent,
} from "react";
import ReactFlow, {
  Background,
  Controls,
  SelectionMode,
  getRectOfNodes,
  getTransformForBounds,
  type Edge,
  type Node,
  type OnConnect,
  type OnEdgesChange,
  type OnInit,
  type NodeMouseHandler,
  type OnNodesChange,
  type OnSelectionChangeParams,
  type ReactFlowInstance,
  type XYPosition,
} from "reactflow";
import "reactflow/dist/style.css";
import type FeatureNodeType from "../features/FeatureNodeType";
import CustomNode from "./CustomNode";
import "./ReactFlowGraph.css";
import {
  gridDotDarkColor,
  gridDotLightColor,
  gridDotSize,
  gridGap,
} from "./styles";

// Zoom limits. The floor is far below React Flow's own so that a wide graph
// can still be fitted into a narrow canvas rather than running off the edge
const minZoom = 0.05;
const maxZoom = 2;
// Share of the canvas left empty around the graph when fitting it
const fitPadding = 0.2;

interface ReactFlowGraphProps {
  nodes: Node[];
  edges: Edge[];
  onInit: OnInit;
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  onSelectionChange: (params: OnSelectionChangeParams) => void;
  onConnect: OnConnect;
  onDropNode: (featureNodeType: FeatureNodeType, position: XYPosition) => void;
  onNodeClick: NodeMouseHandler;
  // Bump to ask for the graph to be fitted into the canvas at whatever size it
  // has right now. 0 means "never asked"
  fitRequestId: number;
  onNodeContextMenu: NodeMouseHandler;
  onPaneContextMenu: (event: ReactMouseEvent) => void;
  isDarkMode: boolean;
  isGridVisible: boolean;
  isSnapToGrid: boolean;
}

const ReactFlowGraph: FunctionComponent<ReactFlowGraphProps> = ({
  nodes,
  edges,
  onInit,
  onNodesChange,
  onEdgesChange,
  onSelectionChange,
  onConnect,
  onDropNode,
  onNodeClick,
  fitRequestId,
  onNodeContextMenu,
  onPaneContextMenu,
  isDarkMode,
  isGridVisible,
  isSnapToGrid,
}) => {
  const theme = useTheme();
  const reactFlowWrapper = useRef<HTMLDivElement | null>(null);
  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance | null>(null);

  // The canvas keeps the app background so the nodes read as cards on a page
  const style = useMemo(
    () => ({ background: theme.palette.background.default }),
    [theme.palette.background.default],
  );
  const nodeTypes = useMemo(() => ({ custom: CustomNode }), []);
  const deleteKeyCode = useMemo(() => ["Backspace", "Delete"], []);
  const multiSelectionKeyCode = useMemo(() => ["Meta", "Control"], []);
  const zoomActivationKeyCode = useMemo(() => ["Meta", "Control"], []);
  // Snapping to the same step the grid is drawn at, so a snapped node lands on
  // a dot the user can actually see
  const snapGrid = useMemo((): [number, number] => [gridGap, gridGap], []);

  const handleInit: OnInit = useCallback(
    (reactFlowInstance: ReactFlowInstance) => {
      onInit(reactFlowInstance);

      setReactFlowInstance(reactFlowInstance);
    },
    [onInit],
  );

  /**
   * Fits the graph into the canvas whenever the caller asks for it.
   *
   * The transform is worked out from the wrapper's own measurements instead of
   * going through `fitView`. React Flow keeps its idea of the canvas size in a
   * store fed by a ResizeObserver, and an observer reports after the layout
   * that caused it: right after something resizes the canvas that store still
   * holds the old width, so `fitView` would fit the graph to a canvas that no
   * longer exists. Reading the DOM in a layout effect gets the width that's
   * actually there.
   *
   * Only the request id is watched. The point is to fit when asked, not
   * whenever the nodes happen to move.
   */
  useLayoutEffect(() => {
    if (fitRequestId === 0 || reactFlowInstance === null) {
      return;
    }
    const wrapper = reactFlowWrapper.current;
    if (wrapper === null) {
      return;
    }

    const { width, height } = wrapper.getBoundingClientRect();
    const nodesBounds = getRectOfNodes(nodes);
    // An empty graph has nothing to frame, and a canvas with no area would
    // divide by zero
    if (width === 0 || height === 0 || nodesBounds.width === 0) {
      return;
    }

    const [x, y, zoom] = getTransformForBounds(
      nodesBounds,
      width,
      height,
      minZoom,
      maxZoom,
      fitPadding,
    );
    reactFlowInstance.setViewport({ x, y, zoom });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitRequestId]);

  const handleDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  /**
   * Add a new node when dropping a node from feature panel.
   *
   * Reference: https://reactflow.dev/docs/examples/interaction/drag-and-drop/
   */
  const handleDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();

      if (reactFlowWrapper.current === null || reactFlowInstance === null) {
        return;
      }

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const featureNodeTypeJsonData = event.dataTransfer.getData(
        "application/reactflow",
      );
      let featureNodeType: FeatureNodeType;
      try {
        featureNodeType = JSON.parse(featureNodeTypeJsonData);
      } catch (error) {
        // Ignore dropping unknown data, e.g., the data would be empty when
        // dragging any selected input text in custom node to the canvas
        return;
      }

      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      onDropNode(featureNodeType, position);
    },
    [onDropNode, reactFlowInstance],
  );

  return (
    <Box
      display="flex"
      flexGrow={1}
      sx={{
        "> div": { flexGrow: 1 },
      }}
    >
      <div ref={reactFlowWrapper}>
        <ReactFlow
          // Common props
          nodes={nodes}
          edges={edges}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          style={style}
          className={isDarkMode ? "dark-mode" : ""}
          // Viewport props
          minZoom={minZoom}
          maxZoom={maxZoom}
          // Event handlers: General events
          onInit={handleInit}
          // Event handlers: Node events
          onNodesChange={onNodesChange}
          onNodeClick={onNodeClick}
          onNodeContextMenu={onNodeContextMenu}
          // Event handlers: Pane events
          onPaneContextMenu={onPaneContextMenu}
          // Event handlers: Edge events
          onEdgesChange={onEdgesChange}
          // Event handlers: Selection events
          onSelectionChange={onSelectionChange}
          // Event handlers: Drag and drop events
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          // Keyboard props
          deleteKeyCode={deleteKeyCode}
          multiSelectionKeyCode={multiSelectionKeyCode}
          zoomActivationKeyCode={zoomActivationKeyCode}
          // Selection props
          selectionOnDrag={true}
          panOnDrag={[1, 2]}
          selectionMode={SelectionMode.Partial}
          // Grid props
          snapToGrid={isSnapToGrid}
          snapGrid={snapGrid}
        >
          {isGridVisible && (
            <Background
              gap={gridGap}
              size={gridDotSize}
              color={isDarkMode ? gridDotDarkColor : gridDotLightColor}
            />
          )}
          <Controls />
        </ReactFlow>
      </div>
    </Box>
  );
};

export default ReactFlowGraph;
